import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag } from "lucide-react";
import { api, eur } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { ProductCard } from "@/components/ProductCard";
import { SizeGuide } from "@/components/SizeGuide";

const G = "/img/gear";
const ITEMS = [
  { key: "chaqueta", slug: "gear-chaqueta-softshell", name: "Chaqueta Softshell", tag: "Cortavientos · Repelente al agua", text: "Rojo FAUNNY con paneles verde bosque, escudo en la espalda y bolsillos con cremallera. Para las mañanas frías en la cuadra.", model: `${G}/chaqueta-modelo.jpg`, pack: `${G}/chaqueta.jpg` },
  { key: "mochila", slug: "gear-mochila-rider", name: "Mochila Rider", tag: "Compartimentos acolchados", text: "Negra con ribetes rojos y blancos, bolsillos laterales y espacio para todo lo que llevas a la pista o a la competición.", model: `${G}/mochila-modelo.jpg`, pack: `${G}/mochila.jpg` },
  { key: "camiseta", slug: "gear-camiseta-raglan", name: "Camiseta Raglan", tag: "Algodón transpirable", text: "Blanca con mangas azul FAUNNY y logo en el pecho. Cómoda para trabajar, montar o animar desde la grada.", model: `${G}/camiseta-modelo.jpg` },
];

const Buy = ({ product, id }) => {
  const { add } = useCart();
  const [sel, setSel] = useState(0);
  if (!product) return null;
  const fmt = product.formats[sel];
  const sizes = product.formats.length > 1;
  const out = !fmt || fmt.stock <= 0;
  return (
    <div className="flex flex-wrap items-center gap-4">
      <span className="font-display font-black text-4xl text-[#DC2626]" data-testid={`gear-price-${id}`}>{eur(fmt?.price)}</span>
      <span className="text-xs text-slate-500">IVA incluido{sizes ? "" : ` · ${fmt?.label}`}</span>
      {sizes && (
        <div className="w-full flex flex-wrap items-center gap-2" data-testid={`gear-sizes-${id}`}>
          <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mr-1">Talla</span>
          {product.formats.map((f, i) => (
            <button key={f.id} onClick={() => setSel(i)} disabled={f.stock <= 0}
              className={`min-w-[3rem] h-10 px-3 rounded-full border text-sm font-bold transition-colors disabled:opacity-30 disabled:line-through ${i === sel ? "bg-[#0B1D3A] border-[#0B1D3A] text-white" : "border-slate-300 text-[#0B1D3A] hover:border-[#0B1D3A]"}`}
              data-testid={`gear-size-${id}-${f.id}`}>{f.label}</button>
          ))}
          <span className="ml-2"><SizeGuide id={id} /></span>
        </div>
      )}
      <div className="w-full flex flex-wrap gap-3 mt-2">
        <button disabled={out} onClick={() => add(product, fmt)} className="rounded-full bg-[#DC2626] hover:bg-[#B91C1C] text-white font-display font-bold uppercase tracking-wide text-sm px-7 py-3 flex items-center gap-2 transition-colors disabled:opacity-50" data-testid={`gear-add-${id}`}>
          <ShoppingBag className="w-4 h-4" /> {out ? "Agotado" : "Añadir al carrito"}
        </button>
        <Link to={`/producto/${product.slug}`} className="rounded-full border border-[#0B1D3A] text-[#0B1D3A] hover:bg-[#0B1D3A] hover:text-white font-display font-bold uppercase tracking-wide text-sm px-6 py-3 transition-colors" data-testid={`gear-details-${id}`}>Ver detalles</Link>
      </div>
    </div>
  );
};

const Item = ({ it, i, product }) => (
  <article className={`grid md:grid-cols-2 gap-8 lg:gap-14 items-center ${i % 2 ? "md:[&>*:first-child]:order-2" : ""}`} data-testid={`gear-item-${it.key}`}>
    <div className="relative">
      <img src={it.model} alt={it.name} className="w-full aspect-square object-cover rounded" />
      {it.pack && <img src={it.pack} alt="" className="hidden sm:block absolute -bottom-6 -right-4 w-40 lg:w-48 aspect-square object-cover rounded border-4 border-white shadow-xl" />}
      <span className="absolute top-4 left-4 bg-[#DC2626] text-white text-xs font-bold uppercase tracking-[0.2em] px-3 py-1.5">Nuevo</span>
    </div>
    <div>
      <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#DC2626] mb-3">{it.tag}</p>
      <h2 className="font-display font-black uppercase text-4xl lg:text-5xl text-[#0B1D3A] mb-4">{it.name}</h2>
      <p className="text-slate-600 leading-relaxed mb-8 max-w-md">{it.text}</p>
      <Buy product={product} id={it.key} />
    </div>
  </article>
);

export default function Gear() {
  const [prods, setProds] = useState({});
  useEffect(() => { api.get("/products", { params: { line: "gear" } }).then((r) => setProds(Object.fromEntries(r.data.map((p) => [p.slug, p])))); }, []);
  return (
    <div data-testid="gear-page">
      <section className="bg-[#0B1D3A] text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 py-16 lg:py-24 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
          <div>
            <span className="inline-block bg-[#DC2626] text-xs font-bold uppercase tracking-[0.25em] px-3 py-1.5 mb-6">Nueva colección · Ya a la venta</span>
            <h1 className="font-display font-black uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mb-6" data-testid="gear-title">FAUNNY <span className="text-[#60A5FA]">Gear</span></h1>
            <p className="text-blue-100 text-lg max-w-lg mb-10">Ropa y accesorios para quien vive el caballo cada día. Suman a tu pedido para el envío gratis y los descuentos por volumen.</p>
            <a href="#coleccion" className="inline-block rounded-full bg-[#DC2626] hover:bg-[#B91C1C] font-display font-bold uppercase tracking-wide text-sm px-7 py-3 transition-colors" data-testid="gear-hero-cta">Ver la colección</a>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <img src={`${G}/chaqueta-modelo.jpg`} alt="Chaqueta FAUNNY Gear" className="col-span-2 w-full h-64 sm:h-80 object-cover object-top rounded" />
            <img src={`${G}/mochila-modelo.jpg`} alt="Mochila FAUNNY Gear" className="w-full h-44 sm:h-56 object-cover rounded" />
            <img src={`${G}/camiseta-modelo.jpg`} alt="Camiseta FAUNNY Gear" className="w-full h-44 sm:h-56 object-cover object-top rounded" />
          </div>
        </div>
      </section>
      <section id="coleccion" className="max-w-6xl mx-auto px-4 py-20 space-y-24 scroll-mt-24">
        {ITEMS.map((it, i) => <Item key={it.key} it={it} i={i} product={prods[it.slug]} />)}
      </section>
      <section className="bg-[#EFF6FF] py-20" data-testid="gear-packs">
        <div className="max-w-7xl mx-auto px-4">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#DC2626] mb-3">Ideales para regalar</p>
          <h2 className="font-display font-black uppercase text-4xl lg:text-5xl text-[#0B1D3A] mb-10">Packs regalo</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.values(prods).filter((p) => p.bundle).map((p) => <ProductCard key={p.slug} p={p} />)}
          </div>
        </div>
      </section>
    </div>
  );
}
